import { SearchOutlined } from '@ant-design/icons'
import { Empty, Input, Pagination, Select, Skeleton, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  duplicateContentProject,
  listContentProjectMemberOptions,
  listContentProjectMemberProjects,
  type ContentProjectMemberOption,
  type ContentProjectSort,
  type ContentProjectSummary,
  type SaasApiError,
} from '../../api'
import VisualProjectCard from '../contentProduction/gallery/VisualProjectCard'
import '../contentProduction/content-production.css'

/**
 * VistoeraTASK-00556 — 团队管理 → 成员项目 (Workspace OWNER only).
 *
 * Lazy by construction: this component is only mounted by the
 * `/team/member-projects` route, so nothing is requested while the tab is
 * closed. The Owner sees other members' projects as metadata cards; opening one
 * goes to the SAME Canvas in READ_ONLY mode (the server decides — an Owner can
 * never edit, delete, re-share or copy someone's PRIVATE project). The API
 * independently answers 403 to everyone but the Owner.
 */
const PAGE_SIZE = 16
const ALL_MEMBERS = '__all__'
const UNKNOWN_CREATOR = 'unknown'
const SORTS: ContentProjectSort[] = ['created_desc', 'created_asc', 'updated_desc']

export default function TeamMemberProjectsTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation(['contentProjects', 'team'])
  const navigate = useNavigate()

  const [members, setMembers] = useState<ContentProjectMemberOption[]>([])
  const [unknownCount, setUnknownCount] = useState(0)
  const [creatorId, setCreatorId] = useState<string>(ALL_MEMBERS)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [sort, setSort] = useState<ContentProjectSort>('created_desc')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<ContentProjectSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [copyingId, setCopyingId] = useState<string | null>(null)
  const requestSeq = useRef(0)

  // Member filter options — one request, on mount only.
  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    void (async () => {
      try {
        const { data } = await listContentProjectMemberOptions(workspaceId)
        if (cancelled) return
        setMembers(data.members)
        setUnknownCount(data.unknown_project_count)
      } catch (e) {
        if (!cancelled) message.error((e as SaasApiError).message || t('contentProjects:team.memberProjects.optionsFailed'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [workspaceId, t])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [q])

  const load = useCallback(async () => {
    if (!workspaceId) return
    const seq = ++requestSeq.current
    setLoading(true)
    setLoadFailed(false)
    try {
      const { data } = await listContentProjectMemberProjects(workspaceId, {
        creator_id: creatorId === ALL_MEMBERS ? undefined : creatorId,
        q: debouncedQ || undefined,
        sort,
        page,
        page_size: PAGE_SIZE,
      })
      if (seq !== requestSeq.current) return
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      if (seq !== requestSeq.current) return
      setLoadFailed(true)
      setItems([])
      setTotal(0)
      message.error((e as SaasApiError).message || t('contentProjects:team.memberProjects.loadFailed'))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [workspaceId, creatorId, debouncedQ, sort, page, t])

  useEffect(() => {
    void load()
  }, [load])

  const nameCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const m of members) counts.set(m.display_name, (counts.get(m.display_name) ?? 0) + 1)
    return counts
  }, [members])

  const memberLabel = useCallback(
    (m: ContentProjectMemberOption) => {
      const duplicate = (nameCounts.get(m.display_name) ?? 0) > 1
      const parts = [m.display_name]
      if (duplicate && m.email) parts.push(`(${m.email})`)
      if (m.is_self) parts.push(`· ${t('contentProjects:team.memberProjects.self')}`)
      parts.push(`· ${m.project_count}`)
      return parts.join(' ')
    },
    [nameCounts, t],
  )

  const selectOptions = useMemo(() => {
    const active = members.filter((m) => m.status === 'active')
    const departed = members.filter((m) => m.status === 'departed')
    const groups: Array<{ label: string; options: Array<{ value: string; label: string }> }> = []
    if (active.length > 0) {
      groups.push({
        label: t('contentProjects:team.memberProjects.activeGroup'),
        options: active.map((m) => ({ value: m.user_id, label: memberLabel(m) })),
      })
    }
    if (departed.length > 0) {
      groups.push({
        label: t('contentProjects:team.memberProjects.departedGroup'),
        options: departed.map((m) => ({
          value: m.user_id,
          label: `${memberLabel(m)} (${t('contentProjects:team.memberProjects.departed')})`,
        })),
      })
    }
    if (unknownCount > 0) {
      groups.push({
        label: t('contentProjects:team.memberProjects.unknownGroup'),
        options: [
          {
            value: UNKNOWN_CREATOR,
            label: `${t('contentProjects:team.memberProjects.unknownCreator')} · ${unknownCount}`,
          },
        ],
      })
    }
    return [{ value: ALL_MEMBERS, label: t('contentProjects:team.memberProjects.allMembers') }, ...groups]
  }, [members, unknownCount, memberLabel, t])

  const openProject = (projectId: string) => {
    navigate(`/workspace/${workspaceId}/content-production/${projectId}/canvas`)
  }

  const onCopy = (project: ContentProjectSummary) => {
    if (copyingId) return
    void (async () => {
      setCopyingId(project.id)
      try {
        const { data } = await duplicateContentProject(workspaceId, project.id)
        const restricted = data.copy_report?.restricted_asset_count ?? 0
        message.success(
          restricted > 0
            ? t('contentProjects:team.toast.copiedRestricted', { count: restricted })
            : t('contentProjects:team.toast.copiedToMine'),
        )
        if (data?.id) openProject(data.id)
      } catch (e) {
        message.error((e as SaasApiError).message || t('contentProjects:toast.copyFailedGeneric'))
      } finally {
        setCopyingId(null)
      }
    })()
  }

  return (
    <div className="cpg-page tm-member-projects" data-testid="team-member-projects">
      <section className="tm-card">
        <div className="tm-card-head">
          <h2 className="tm-card-title">{t('contentProjects:team.memberProjects.title')}</h2>
          <span className="tm-hint">{t('contentProjects:team.memberProjects.description')}</span>
        </div>

        <div className="cpg-member-toolbar tm-toolbar">
          <Select
            style={{ minWidth: 240 }}
            value={creatorId}
            options={selectOptions}
            onChange={(v) => {
              setCreatorId(v)
              setPage(1)
            }}
            aria-label={t('contentProjects:team.memberProjects.memberFilterAria')}
            data-testid="member-projects-member-filter"
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('contentProjects:team.memberProjects.searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 240 }}
            data-testid="member-projects-search"
          />
          <div className="tm-toolbar-spacer" />
          <Select
            style={{ width: 160 }}
            value={sort}
            options={SORTS.map((s) => ({ value: s, label: t(`contentProjects:team.memberProjects.sort.${s}`) }))}
            onChange={(v) => {
              setSort(v)
              setPage(1)
            }}
            aria-label={t('contentProjects:team.memberProjects.sortAria')}
            data-testid="member-projects-sort"
          />
        </div>

        <p className="tm-hint" style={{ marginBottom: 12 }}>
          {t('contentProjects:team.memberProjects.readOnlyNote')}
        </p>

        {loading && items.length === 0 ? (
          <div className="cpg-gallery-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="cpg-card cpg-card--skeleton">
                <Skeleton.Image active style={{ width: '100%', height: 180 }} />
                <Skeleton active paragraph={{ rows: 1 }} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              loadFailed ? t('contentProjects:team.memberProjects.loadFailed') : t('contentProjects:team.memberProjects.empty')
            }
            style={{ padding: '48px 0' }}
          />
        ) : (
          <>
            <div className="cpg-gallery-grid" data-testid="member-projects-grid">
              {items.map((item) => (
                <VisualProjectCard
                  key={item.id}
                  project={item}
                  managementView
                  copying={copyingId === item.id}
                  deleting={false}
                  selected={false}
                  selectionActive={false}
                  onOpen={() => openProject(item.id)}
                  onCopy={() => onCopy(item)}
                  onDelete={() => undefined}
                  onToggleSelect={() => undefined}
                />
              ))}
            </div>
            {total > PAGE_SIZE ? (
              <div className="cpg-pagination">
                <Pagination current={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} showSizeChanger={false} />
              </div>
            ) : null}
          </>
        )}
      </section>
    </div>
  )
}
